import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/utils/cn';
import { Button } from './Button';

type ModalAnimation = 'none' | 'scale' | 'slide' | 'fade';

/**
 * Modal Component Usage Guidelines
 *
 * DO:
 * - Use the `title` prop for modal titles (don't render your own)
 * - Use the `description` prop for subtitles/descriptions
 * - Let the Modal handle the close button (showCloseButton defaults to true)
 * - Use `scrollBehavior="inside"` for long forms or content
 * - Use appropriate `size` prop: 'sm' | 'md' | 'lg' | 'xl'
 * - Use `animation` prop for animated modals: 'scale' | 'slide' | 'fade'
 *
 * DON'T:
 * - Render your own title inside the modal content
 * - Add duplicate close buttons
 * - Wrap content in unnecessary containers
 * - Use empty title prop (title="")
 *
 * Example:
 * ```tsx
 * <Modal
 *   isOpen={isOpen}
 *   onClose={handleClose}
 *   title="Create New Item"
 *   description="Add a new item to your list"
 *   size="lg"
 *   scrollBehavior="inside"
 *   animation="scale"
 * >
 *   <YourContent />
 * </Modal>
 * ```
 */
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showCloseButton?: boolean;
  scrollBehavior?: 'inside' | 'outside';
  animation?: ModalAnimation;
}

// Animation variants for framer-motion
const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

const modalVariants = {
  scale: {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
      }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: {
        duration: 0.2,
      }
    },
  },
  slide: {
    hidden: { opacity: 0, y: -50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
      }
    },
    exit: {
      opacity: 0,
      y: -50,
      transition: {
        duration: 0.2,
      }
    },
  },
  fade: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
      }
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.2,
      }
    },
  },
};

const CloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  className,
  size = 'md',
  showCloseButton = true,
  scrollBehavior = 'outside',
  animation = 'none',
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Focus trap effect
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const modalElement = modalRef.current;
    const focusableElements = modalElement.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    // Focus the first focusable element when modal opens
    if (firstFocusable) {
      firstFocusable.focus();
    }

    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      if (event.shiftKey) {
        if (document.activeElement === firstFocusable) {
          event.preventDefault();
          lastFocusable?.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          event.preventDefault();
          firstFocusable?.focus();
        }
      }
    };

    modalElement.addEventListener('keydown', handleTabKey);

    return () => {
      modalElement.removeEventListener('keydown', handleTabKey);
    };
  }, [isOpen]);

  const sizeClasses = {
    sm: 'modal-size-sm',
    md: 'modal-size-md',
    lg: 'modal-size-lg',
    xl: 'modal-size-xl',
  };

  const animatedSizeClasses = {
    sm: 'max-w-md w-full',
    md: 'max-w-lg w-full',
    lg: 'max-w-2xl w-full',
    xl: 'max-w-4xl w-full',
  };

  // Render animated modal with framer-motion
  if (animation !== 'none') {
    const variants = modalVariants[animation];

    const animatedModalContent = (
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={onClose}
              variants={backdropVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              transition={{ duration: 0.3 }}
              aria-hidden="true"
            />

            {/* Modal */}
            <motion.div
              ref={modalRef}
              className={cn(
                "modal-panel",
                animatedSizeClasses[size],
                className
              )}
              role="dialog"
              aria-modal="true"
              aria-labelledby={title ? "modal-title" : undefined}
              aria-describedby={description ? "modal-description" : undefined}
              variants={variants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {/* Header */}
              {(title || showCloseButton) && (
                <motion.div
                  className="layout-row-between card-content border-b"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.3 }}
                >
                  <div>
                    {title && (
                      <h2 id="modal-title" className="text-heading-md text-foreground">
                        {title}
                      </h2>
                    )}
                    {description && (
                      <p id="modal-description" className="text-muted-sm mt-1">
                        {description}
                      </p>
                    )}
                  </div>
                  {showCloseButton && (
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={onClose}
                        className="h-8 w-8"
                        aria-label="Close modal"
                      >
                        <CloseIcon />
                      </Button>
                    </motion.div>
                  )}
                </motion.div>
              )}

              {/* Content */}
              <motion.div
                className="card-content"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.3 }}
              >
                {children}
              </motion.div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    );

    return createPortal(animatedModalContent, document.body);
  }

  // Non-animated modal (original behavior)
  if (!isOpen) return null;

  const modalContent = scrollBehavior === 'inside' ? (
    // Inside scrolling: Modal has fixed height and content scrolls inside
    <div className="modal-container">
      {/* Backdrop */}
      <div
        className="modal-backdrop modal-backdrop-animate"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal with internal scroll */}
      <div
        ref={modalRef}
        className={cn(
          "modal-panel modal-panel-inside modal-animate",
          sizeClasses[size],
          className
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? "modal-title" : undefined}
        aria-describedby={description ? "modal-description" : undefined}
      >
        {/* Header - Fixed */}
        {(title || showCloseButton) && (
          <div className="modal-header-fixed">
            <div>
              {title && (
                <h2 id="modal-title" className="modal-title">
                  {title}
                </h2>
              )}
              {description && (
                <p id="modal-description" className="modal-description">
                  {description}
                </p>
              )}
            </div>
            {showCloseButton && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="modal-close-btn"
                aria-label="Close modal"
              >
                <CloseIcon />
              </Button>
            )}
          </div>
        )}

        {/* Content - Scrollable */}
        <div className="modal-body-scrollable">
          {children}
        </div>
      </div>
    </div>
  ) : (
    // Outside scrolling: Entire modal scrolls with the page
    <div className="modal-container-scroll">
      {/* Backdrop */}
      <div
        className="modal-backdrop modal-backdrop-animate"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal container with scroll */}
      <div className="modal-scroll-wrapper">
        {/* Modal */}
        <div
          ref={modalRef}
          className={cn(
            "modal-panel modal-panel-outside modal-animate",
            sizeClasses[size],
            className
          )}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? "modal-title" : undefined}
          aria-describedby={description ? "modal-description" : undefined}
        >
          {/* Header */}
          {(title || showCloseButton) && (
            <div className="modal-header">
              <div>
                {title && (
                  <h2 id="modal-title" className="modal-title">
                    {title}
                  </h2>
                )}
                {description && (
                  <p id="modal-description" className="text-muted-sm mt-1">
                    {description}
                  </p>
                )}
              </div>
              {showCloseButton && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="modal-close-btn"
                  aria-label="Close modal"
                >
                  <CloseIcon />
                </Button>
              )}
            </div>
          )}

          {/* Content */}
          <div className="modal-body">
            {children}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
